// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useRef } from 'react';

import type { BendTypeName } from '@evolvemep/api-ts';
import { Flex } from '@mantine/core';
import { useResizeObserver } from '@mantine/hooks';

import { isNil, isNotNil } from 'helpers/isNotNil';
import { BendLayoutGeometry } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendGeometries/BendLayoutGeometry';
import type { BendPreviewSpecifications } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import {
  BendPreviewRenderer,
  type FitInsets,
  type RenderMode,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewRenderer';
import { getBendPreview } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/getBendPreview';
import { useBendPreviewContext } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';

type Props = {
  bendType: BendTypeName;
  /** The bend's dimensions. When null, a bend type preview is drawn from default dimensions. */
  bendPreviewData: BendPreviewSpecifications | null;
  renderMode: RenderMode;
  /**
   * When true, shows the bend labels.
   * @defaultValue true
   */
  showLabels?: boolean;
  /**
   * When true, shows the bend layout (flat tube with marks) instead of bent shape.
   * @defaultValue false
   */
  showLayout?: boolean;
  /**
   * Space at each edge of the canvas covered by other UI, such as floating panels. The bend is fit and centered
   * in the remaining area, and labels are kept inside it.
   */
  fitInsets?: FitInsets;
  /**
   * When true, the 3D view slowly orbits the bend. The orbit stops for good when the user moves the view, or when
   * this turns false, which also turns the camera back to its starting angle.
   * @defaultValue false
   */
  autoOrbit?: boolean;
};

/**
 * Renders a bend preview into a canvas that fills its container.
 * Owns the {@link BendPreviewRenderer} lifecycle; view controls are left to the caller.
 * Must be rendered inside a `BendPreviewContextProvider`, which carries label highlighting.
 */
export const BendRenderCanvas = ({
  bendType,
  bendPreviewData,
  renderMode,
  showLabels = true,
  showLayout = false,
  fitInsets,
  autoOrbit = false,
}: Props) => {
  const { highlightedObject, setHighlightedObject } = useBendPreviewContext();

  const renderer = useRef<BendPreviewRenderer | null>(null);
  // Set the animation loop.
  useEffect(() => renderer.current?.animate(renderMode), [renderMode]);

  // Update the highlighted object when the context changes
  useEffect(() => {
    renderer.current?.updateHighlightedObject(highlightedObject);
  }, [highlightedObject]);

  // Update geometry when input props change
  useEffect(() => {
    if (isNil(renderer.current) || isNil(bendType)) return undefined;
    const bendGeometry = showLayout ? new BendLayoutGeometry() : getBendPreview(bendType);
    bendGeometry.createBendPreview(bendPreviewData);
    renderer.current.setGeometry(bendGeometry);
    return () => {
      renderer.current?.clearGeometry();
      bendGeometry.dispose();
    };
  }, [bendPreviewData, bendType, showLayout]);

  useEffect(() => {
    renderer.current?.setLabelsVisible(showLabels);
  }, [showLabels]);

  useEffect(() => {
    if (autoOrbit) {
      renderer.current?.startAutoOrbit();
    } else {
      renderer.current?.endAutoOrbit();
    }
  }, [autoOrbit]);

  const { top = 0, right = 0, bottom = 0, left = 0 } = fitInsets ?? {};
  useEffect(() => {
    renderer.current?.setFitInsets({ top, right, bottom, left });
  }, [top, right, bottom, left]);

  // Clean up the renderer on unmount
  useEffect(
    () => () => {
      renderer.current?.dispose();
      renderer.current = null;
    },
    [],
  );

  const [ref, rect] = useResizeObserver();

  // Resize logic
  useEffect(() => {
    renderer.current?.updateSize(rect.width, rect.height);
  }, [rect.width, rect.height]);

  return (
    <Flex flex={1} align="center" justify="center" style={{ overflow: 'hidden', cursor: 'crosshair' }} ref={ref}>
      <canvas
        style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
        ref={(canvas) => {
          if (isNil(canvas) || isNotNil(renderer.current)) return;
          renderer.current = new BendPreviewRenderer(canvas, setHighlightedObject);
          const { width, height } = canvas.getBoundingClientRect();
          renderer.current.updateSize(width, height);
        }}
      />
    </Flex>
  );
};
